import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import AppShell from './components/layout/AppShell';
import SessionStateScreen from './components/auth/SessionStateScreen';
import { useAuth } from './providers/auth-provider';
import { useConfig } from './providers/config-provider';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import CallsPage from './pages/CallsPage';
import TablesPage from './pages/TablesPage';
import AreasPage from './pages/AreasPage';
import StaffPage from './pages/StaffPage';
import RestaurantsPage from './pages/RestaurantsPage';
import CompaniesPage from './pages/CompaniesPage';
import IntegrationsPage from './pages/IntegrationsPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import MySubscriptionPage from './pages/MySubscriptionPage';
import HelpPage from './pages/HelpPage';
import PanelLoginPage from './pages/PanelLoginPage';
import PanelDisplayPage from './pages/PanelDisplayPage';

const RequireAuth = ({ children }) => {
  const { isAuthenticated, isLoading, logout, retrySession, sessionIssue } = useAuth();

  if (isLoading) {
    return (
      <SessionStateScreen
        variant="loading"
        title="Validando sua sessão"
        message="Estamos conferindo o acesso ativo e reaplicando o escopo operacional antes de liberar o painel."
        primaryAction={{
          label: 'Aguarde',
          disabled: true,
          icon: <ArrowPathIcon className="h-4 w-4 animate-spin" />,
        }}
      />
    );
  }

  if (sessionIssue?.type === 'recoverable') {
    return (
      <SessionStateScreen
        variant="recoverable"
        title={sessionIssue.title}
        message={sessionIssue.message}
        primaryAction={{
          label: 'Tentar novamente',
          onClick: retrySession,
          icon: <ArrowPathIcon className="h-4 w-4" />,
        }}
        secondaryAction={
          isAuthenticated
            ? {
                label: 'Encerrar sessão',
                onClick: logout,
              }
            : undefined
        }
      />
    );
  }

  if (sessionIssue?.type === 'blocked') {
    return (
      <SessionStateScreen
        variant="blocked"
        title={sessionIssue.title}
        message={sessionIssue.message}
        primaryAction={{
          label: 'Atualizar acesso',
          onClick: retrySession,
          icon: <ArrowPathIcon className="h-4 w-4" />,
        }}
        secondaryAction={{
          label: 'Sair',
          onClick: logout,
        }}
      />
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const RequireRestaurant = ({ children }) => {
  const { restaurantId } = useConfig();
  const { user } = useAuth();
  if (!restaurantId) {
    return (
      <section className="rounded-2xl border border-amber-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <h2 className="text-base font-semibold text-amber-900">Restaurante não selecionado</h2>
            <p className="mt-1 text-sm text-amber-800">
              Selecione um restaurante no seletor da barra lateral para liberar os módulos operacionais.
            </p>
          </div>
        </div>

        {(user?.role === 'Admin' || user?.role === 'AdminMaster') ? (
          <div className="mt-4">
            <Link
              to="/app/restaurants"
              className="inline-flex rounded-md bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700"
            >
              Ir para Restaurantes
            </Link>
          </div>
        ) : null}
      </section>
    );
  }
  return children;
};

const RequireRoles = ({ roles = [], children }) => {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (user.role === 'AdminMaster') {
    return children;
  }
  if (!roles.length || roles.includes(user.role)) {
    return children;
  }
  return <Navigate to="/app/calls" replace />;
};

const App = () => {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/painel/login" element={<PanelLoginPage />} />
      <Route
        path="/painel"
        element={
          <RequireAuth>
            <PanelDisplayPage />
          </RequireAuth>
        }
      />
      <Route
        path="/app"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="calls" replace />} />
        <Route
          path="calls"
          element={
            <RequireRestaurant>
              <CallsPage />
            </RequireRestaurant>
          }
        />
        <Route
          path="tables"
          element={
            <RequireRoles roles={['Admin', 'Manager', 'AdminMaster']}>
              <RequireRestaurant>
                <TablesPage />
              </RequireRestaurant>
            </RequireRoles>
          }
        />
        <Route
          path="areas"
          element={
            <RequireRoles roles={['Admin', 'Manager', 'AdminMaster']}>
              <RequireRestaurant>
                <AreasPage />
              </RequireRestaurant>
            </RequireRoles>
          }
        />
        <Route
          path="staff"
          element={
            <RequireRoles roles={['Admin', 'Manager', 'AdminMaster']}>
              <RequireRestaurant>
                <StaffPage />
              </RequireRestaurant>
            </RequireRoles>
          }
        />
        <Route
          path="restaurants"
          element={
            <RequireRoles roles={['Admin', 'AdminMaster']}>
              <RestaurantsPage />
            </RequireRoles>
          }
        />
        <Route
          path="companies"
          element={
            <RequireRoles roles={['AdminMaster']}>
              <CompaniesPage />
            </RequireRoles>
          }
        />
        <Route
          path="integrations"
          element={
            <RequireRoles roles={['AdminMaster']}>
              <IntegrationsPage />
            </RequireRoles>
          }
        />
        <Route
          path="subscriptions"
          element={
            <RequireRoles roles={['AdminMaster']}>
              <SubscriptionsPage />
            </RequireRoles>
          }
        />
        <Route
          path="my-subscription"
          element={
            <RequireRoles roles={['Admin']}>
              <MySubscriptionPage />
            </RequireRoles>
          }
        />
        <Route path="help" element={<HelpPage />} />
        <Route path="*" element={<Navigate to="calls" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
};

export default App;
